import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import SelectionDrawer from './SelectionDrawer';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { formatSize } from '../utils/formatters';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom marker icons
const createCustomIcon = (color = '#3B82F6') => {
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        background-color: ${color};
        width: 30px;
        height: 30px;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        border: 3px solid white;
        box-shadow: 0 2px 5px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <div style="
          transform: rotate(45deg);
          color: white;
          font-weight: bold;
          font-size: 14px;
        ">🏠</div>
      </div>
    `,
    iconSize: [30, 30],
    iconAnchor: [15, 30],
  });
};

// Component to handle map clicks (including mobile tap)
const MapClickHandler = ({ onMapClick }) => {
  const touchStateRef = useRef({
    startX: 0,
    startY: 0,
    moved: false,
    startTime: 0,
  });

  const map = useMapEvents({
    click: (e) => {
      if (onMapClick) {
        onMapClick(e.latlng);
      }
    },
    touchstart: (e) => {
      const oe = e.originalEvent;
      const touch = oe?.touches?.[0];
      if (!touch) return;
      touchStateRef.current = {
        startX: touch.clientX,
        startY: touch.clientY,
        moved: false,
        startTime: Date.now(),
      };
    },
    touchmove: (e) => {
      const oe = e.originalEvent;
      const touch = oe?.touches?.[0];
      if (!touch) return;
      const dx = touch.clientX - touchStateRef.current.startX;
      const dy = touch.clientY - touchStateRef.current.startY;
      if (Math.hypot(dx, dy) > 10) {
        touchStateRef.current.moved = true;
      }
    },
    touchend: (e) => {
      if (!onMapClick) return;
      const duration = Date.now() - touchStateRef.current.startTime;
      if (touchStateRef.current.moved || duration > 600) return;

      const latlng = (() => {
        if (e.latlng) return e.latlng;
        const oe = e.originalEvent;
        const touch = oe?.changedTouches?.[0] || oe?.touches?.[0];
        if (touch && typeof touch.clientX === 'number' && typeof touch.clientY === 'number') {
          try {
            const point = map.mouseEventToContainerPoint(touch);
            return map.containerPointToLatLng(point);
          } catch (err) {
            return null;
          }
        }
        if (oe && typeof oe.clientX === 'number' && typeof oe.clientY === 'number') {
          try {
            return map.mouseEventToLatLng(oe);
          } catch (err) {
            return null;
          }
        }
        return null;
      })();

      if (latlng) {
        onMapClick(latlng);
      }
    },
  });
  return null;
};

const MapComponent = ({
  center = [8.975585, -79.520459], // Default to Panama coordinates
  zoom = 13, // Reasonable zoom level for Panama
  markers = [],
  onMapClick,
  onMarkerClick,
  onMarkerDrag, // New prop for marker drag events
  height = '400px',
  allowClick = true,
  showControls = true,
  className = '',
  focusOn = null, // New prop to focus on specific coordinates
  draggableMarkerId = null, // ID of marker that should be draggable
  bounds = null,
  enableSelection = false,
  selectionBounds = null,
  onSelectionChange = null,
  selectionOptions = {},
  selectionHoldDuration,
  selectionMinLatLngDelta,
}) => {
  const { t } = useTranslation();
  const [mapCenter, setMapCenter] = useState(center);
  const [mapZoom, setMapZoom] = useState(zoom);
  const [isLocating, setIsLocating] = useState(false);
  const mapRef = useRef();
  const prevCenterRef = useRef();
  const prevBoundsRef = useRef();

  // Update map center when prop changes (only if actually different)
  useEffect(() => {
    const centerStr = JSON.stringify(center);
    const prevCenterStr = JSON.stringify(prevCenterRef.current);
    
    if (center && centerStr !== prevCenterStr) {
      setMapCenter(center);
      prevCenterRef.current = center;
    }
  }, [center]);

  // Focus on specific coordinates when focusOn prop changes
  useEffect(() => {
    if (focusOn && focusOn.lat && focusOn.lng) {
      const newCenter = [focusOn.lat, focusOn.lng];
      setMapCenter(newCenter);
      setMapZoom(18); // Close zoom level to focus on the pin
      
      // Smoothly animate to the new position
      if (mapRef.current) {
        mapRef.current.setView(newCenter, 18, {
          animate: true,
          duration: 1.5
        });
      }
    }
  }, [focusOn]);

  // Fit to bounds when provided
  useEffect(() => {
    if (!bounds || !Array.isArray(bounds) || bounds.length !== 2) {
      return;
    }
    const sameBounds = JSON.stringify(bounds) === JSON.stringify(prevBoundsRef.current);
    if (sameBounds) return;
    prevBoundsRef.current = bounds;
    if (mapRef.current && bounds) {
      try {
        mapRef.current.fitBounds(bounds, { padding: [20, 20], maxZoom: 16 });
      } catch (e) {
        // noop
      }
    }
  }, [bounds]);

  // Get user's current location
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error(t('map.geoNotSupported'));
      return;
    }

    setIsLocating(true);
    // Ensure button unblocks even if browser doesn't call callbacks.
    const fallback = setTimeout(() => setIsLocating(false), 12000);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        clearTimeout(fallback);
        setIsLocating(false);
        const { latitude, longitude } = position.coords;
        const newCenter = [latitude, longitude];
        const newZoom = 15;

        setMapCenter(newCenter);
        setMapZoom(newZoom);

        // Ensure the Leaflet map actually moves (MapContainer does not always apply center changes after mount).
        if (mapRef.current) {
          try {
            mapRef.current.setView(newCenter, newZoom, { animate: true, duration: 1.0 });
          } catch (e) {
            // noop
          }
        }
      },
      (error) => {
        clearTimeout(fallback);
        setIsLocating(false);
        console.error('Error getting location:', error);
        const code = error?.code;
        if (code === 1) toast.error(t('map.locationPermissionDenied'));
        else if (code === 2) toast.error(t('map.locationUnavailable'));
        else if (code === 3) toast.error(t('map.locationTimeout'));
        else toast.error(t('map.locationFailed'));
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  // Handle map click
  const handleMapClick = (latlng) => {
    if (allowClick && onMapClick) {
      onMapClick(latlng);
    }
  };

  // Handle marker click
  const handleMarkerClick = (marker) => {
    if (onMarkerClick) {
      onMarkerClick(marker);
    }
  };

  return (
    <div className={`map-container relative ${className}`} style={{ height, width: '100%' }}>
      {showControls && (
        <div className="absolute top-4 right-4 z-[1000] flex flex-col gap-2">
          <button
            onClick={getCurrentLocation}
            disabled={isLocating}
            className={`bg-white text-gray-700 px-3 py-2 rounded-lg shadow-lg border border-gray-200 text-sm font-medium ${
              isLocating ? 'opacity-60 cursor-not-allowed' : 'hover:bg-gray-50'
            }`}
            title={t('map.getCurrentLocation')}
          >
            📍 {isLocating ? t('map.locating') : t('map.myLocation')}
          </button>
        </div>
      )}
      
      <MapContainer
        center={mapCenter}
        zoom={mapZoom}
        style={{ height: '100%', width: '100%' }}
        ref={mapRef}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        {enableSelection && (
          <SelectionDrawer
            selectionBounds={selectionBounds}
            onSelectionChange={onSelectionChange}
            rectangleOptions={selectionOptions}
            holdDuration={selectionHoldDuration}
            minLatLngDelta={selectionMinLatLngDelta}
          />
        )}

        <MapClickHandler onMapClick={handleMapClick} />
        
        {markers.map((marker, index) => (
          <Marker
            key={marker.id || index}
            position={[marker.lat, marker.lng]}
            icon={createCustomIcon(marker.color)}
            draggable={draggableMarkerId === marker.id}
            eventHandlers={{
              click: () => handleMarkerClick(marker),
              dragend: (e) => {
                if (onMarkerDrag && draggableMarkerId === marker.id) {
                  const newPosition = e.target.getLatLng();
                  onMarkerDrag({
                    ...marker,
                    lat: newPosition.lat,
                    lng: newPosition.lng
                  });
                }
              },
            }}
          >
            <Popup>
              <div className="p-2">
                <h3 className="font-bold text-lg mb-2">{marker.title || 'Property'}</h3>
                {marker.description && (
                  <p className="text-gray-600 mb-2">{marker.description}</p>
                )}
                {marker.price && (
                  <p className="text-green-600 font-semibold">${marker.price}</p>
                )}
                {marker.size && (
                  <p className="text-gray-500 text-sm">{formatSize(marker.size, marker.size_unit)}</p>
                )}
                {marker.phone && (
                  <p className="text-blue-600 text-sm">📞 {marker.phone}</p>
                )}
                {marker.whatsapp && (
                  <p className="text-green-600 text-sm">📱 WhatsApp: {marker.whatsapp}</p>
                )}
                {marker.onEdit && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      marker.onEdit(marker);
                    }}
                    className="mt-3 w-full rounded bg-blue-600 px-3 py-1 text-xs font-semibold text-white hover:bg-blue-700"
                  >
                    {marker.editLabel || 'Edit listing'}
                  </button>
                )}
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};

export default MapComponent;





